import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { type Lesson, type Marathon, type Module } from '@/api/courses';
import { usersApi } from '@/api/users';
import { progressApi } from '@/api/progress';
import { useIsMobile } from '@/hooks/useIsMobile';
import { MBlock, MEmpty, MIcon, MRow, MSection } from '@/components/mobile/MobileKit';
import { Card } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { cn } from '@/lib/utils';
import { humanizeError } from '@/lib/errors';
import { curatorGradesByLesson, type CuratorGrade } from '@/lib/lessonFeedback';
import { continueLesson, emptyCourseKey, lessonPosition } from '@/lib/marathonCards';
import { ContinueLearningCard } from '@/components/learning/ContinueLearningCard';
import { LessonFeedbackPanel, LessonGradeChip } from '@/components/lesson/LessonGradeChip';
import { useAuthStore } from '@/stores/auth';

/**
 * Marathon detail (#3) — modules accordion for a single enrolled marathon.
 *
 * The module holding the student's *current* lesson (the first non-passed,
 * available lesson in reading order) is auto-expanded on open, and that lesson
 * is highlighted with a "▶ Бастау" affordance so the student lands exactly
 * where they left off. Passed modules stay collapsed; locked modules are shown
 * dimmed and non-interactive.
 */

/** access_status, tolerant of the older is_available-only shape. */
function statusOf(l: Lesson) {
  return l.access_status ?? (l.is_available ? 'available' : 'locked');
}

/**
 * Locate the "continue here" lesson: the first available (not passed, not
 * locked) lesson in module→lesson reading order. Falls back to the last lesson
 * when everything is passed. Returns the owning module id so it can auto-open.
 */
function findCurrent(modules: Module[]): { moduleId?: string; lessonId?: string } {
  let lastPassedModule: string | undefined;
  let lastPassedLesson: string | undefined;
  for (const mod of modules) {
    for (const l of mod.lessons ?? []) {
      const s = statusOf(l);
      if (s === 'available') return { moduleId: mod.id, lessonId: l.id };
      if (s === 'passed') {
        lastPassedModule = mod.id;
        lastPassedLesson = l.id;
      }
    }
  }
  return { moduleId: lastPassedModule, lessonId: lastPassedLesson };
}

export function StudentMarathonPage() {
  const { t } = useTranslation();
  const { marathonId } = useParams();
  const isMobile = useIsMobile();
  /** Тариф ещё не назначен — уроки просто не открыты, а не «их нет». */
  const waitingStart = useAuthStore((st) => st.user?.awaiting_start === true);

  // Reuse the enrollment-resolved marathon list (title + full lesson tree with
  // per-lesson access_status) — the same source the hub uses, so navigating in
  // is instant from cache.
  const marathons = useQuery({
    queryKey: ['student', 'enrolled-marathons'],
    queryFn: () => usersApi.myEnrolledMarathons(),
  });
  const found = marathons.data?.find((m) => m.id === marathonId);
  const marathon: Marathon | undefined = found;
  // Курс без уроков: «Обучение ещё не началось» — только курсу с записью; открытому — «нет уроков» (02.10.2026).
  const emptyKey = emptyCourseKey(waitingStart, found?.enrolled);

  const progress = useQuery({
    queryKey: ['student', 'marathon-progress', marathonId],
    queryFn: () => progressApi.getMyMarathonProgress(marathonId!),
    enabled: !!marathonId,
  });

  const modules = useMemo(() => marathon?.modules ?? [], [marathon]);
  const current = useMemo(() => findCurrent(modules), [modules]);
  // «Продолжить» в блоке сверху страницы (D23 и блок с главной, 11.09.2026). Без запаса
  // на последний пройденный, как у подсветки «Сейчас»: открытого нет — кнопки нет.
  const next = useMemo(() => continueLesson(modules), [modules]);

  // Оценки куратора по урокам (D11, 10.09.2026). Старый сервер уроков в ответе
  // не присылает — карта пустая, чипов нет, экран как раньше.
  const grades = useMemo(() => curatorGradesByLesson(progress.data), [progress.data]);
  // Из уведомления «куратор оценил урок» приходят с `?feedback=<урок>`: урок
  // раскрыт, его модуль открыт, страница докручена до него.
  const [searchParams] = useSearchParams();
  const feedbackId = searchParams.get('feedback');
  const [expanded, setExpanded] = useState<string | null>(feedbackId);
  const toggleFeedback = (id: string) => setExpanded((cur) => (cur === id ? null : id));
  const feedbackModuleId = feedbackId
    ? modules.find((m) => (m.lessons ?? []).some((l) => l.id === feedbackId))?.id
    : undefined;
  const scrolledToFeedback = useRef(false);
  useEffect(() => {
    if (!feedbackId || scrolledToFeedback.current || !grades.has(feedbackId)) return;
    scrolledToFeedback.current = true;
    document.getElementById(`lesson-${feedbackId}`)?.scrollIntoView({ block: 'center' });
  }, [feedbackId, grades]);

  // Which module is expanded — defaults to the one holding the current lesson.
  const [open, setOpen] = useState<string | null>(null);
  const openId = open ?? feedbackModuleId ?? current.moduleId ?? modules[0]?.id ?? null;

  const pct = Math.round(progress.data?.percent_complete ?? 0);

  // ── Блок «Продолжить обучение», как на главной (решение владельца 11.09.2026) ──
  //
  // ⚠️ ВМЕСТО ШАПКИ, А НЕ ПОД НЕЙ. Шапка показывала название и процент — в блоке они
  // есть, и дважды на экране они читались бы как два разных курса. «Все уроки N» —
  // число уроков в списке ниже, нажатие прокручивает к нему.
  const lessonsCount = modules.reduce((n, m) => n + (m.lessons?.length ?? 0), 0);
  const position = lessonPosition(progress.data, lessonsCount);
  const scrollToLessons = () =>
    document.getElementById('course-lessons')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const courseCard = (m: Marathon, variant: 'desktop' | 'mobile') => (
    <ContinueLearningCard
      variant={variant}
      title={m.title}
      coverUrl={m.cover_url}
      lessonNumber={position.number}
      lessonsTotal={position.total}
      percent={pct}
      continueTo={next ? `/app/lessons/${next.id}` : null}
      allLessons={{ count: lessonsCount, onClick: scrollToLessons }}
    />
  );

  // ── Телефон: плоский список модулей, как в макете ───────────────────────
  //
  // ⚠️ ЗДЕСЬ НЕТ РАСКРЫВАЮЩИХСЯ КАРТОЧЕК. На широком экране модуль разворачивается
  // и показывает свои уроки — на телефоне это два уровня вложенности в узкой
  // колонке, и макет их не предполагает: строка модуля ведёт сразу к тому уроку,
  // на котором человек остановился. Пройденные показывают процент, текущий
  // подсвечен и с кнопкой, закрытые — приглушены и не нажимаются.
  if (isMobile) {
    if (marathons.isLoading) return <div className="h-40 animate-pulse rounded-[26px] bg-surface-container-low" />;
    // Сбой загрузки — это ошибка, а не «курс не найден»: до 02.10.2026 телефон говорил «не найден» при обрыве сети.
    if (marathons.error) return <MEmpty icon="error" title={humanizeError(marathons.error)} />;
    if (!marathon) {
      return <MEmpty icon="school" title={t('marathon.notFound')} />;
    }
    // Уроки всех модулей одним списком: модуль у наших курсов почти всегда
    // один, и разбивка по модулям добавляла шаг, ничего не объясняя.
    const allLessons = modules.flatMap((m) => m.lessons ?? []);
    const passedCount = allLessons.filter((l) => statusOf(l) === 'passed').length;

    return (
      <div className="space-y-3">
        <Link to="/app/lessons" className="flex items-center gap-2.5 pb-1 pt-1.5">
          <span className="grid h-8 w-8 flex-none place-items-center rounded-full bg-surface-container-low text-on-surface">
            <span className="material-symbols-outlined text-[17px]">chevron_left</span>
          </span>
          <span className="text-[12px] font-semibold text-on-surface-variant">{t('marathon.back')}</span>
        </Link>

        {courseCard(marathon, 'mobile')}

        {/* ⚠️ «В марафоне пока нет уроков» звучит как поломка, когда уроки
            просто ещё не открыты куратором. Ждущему ученику говорим прямо. */}
        {modules.length === 0 ? (
          <MEmpty icon="school" title={t(emptyKey)} />
        ) : (
          <div id="course-lessons" className="scroll-mt-4 space-y-3">
            {/* ⚠️ СПИСОК УРОКОВ, А НЕ МОДУЛЕЙ. Заказчик 07.09.2026 нажал
                «Продолжить» и попал на экран «Модули: 0 из 1» с единственной
                строкой «Модульдер» — лишний шаг, за которым всё равно список
                уроков. Он прислал компьютерный экран и сказал: должно быть так.

                Он прав: модуль у наших курсов почти всегда один, и промежуточный
                экран не несёт ничего. Раскрываем уроки сразу.

                ⚠️ ЗАГОЛОВОК — «РАЗДЕЛЫ», А НЕ «МОИ КУРСЫ». Список показывает
                уроки ОДНОГО курса, а назывался «Мои курсы»: заголовок обещал
                выбор, которого здесь нет. */}
            <MSection
              title={t('marathon.sections')}
              right={t('marathon.modulesDone', { done: passedCount, total: allLessons.length })}
            />
            <MBlock>
              {allLessons.map((lesson, i) => {
                const st = statusOf(lesson);
                return (
                  <MRow
                    key={lesson.id}
                    last={i === allLessons.length - 1}
                    dim={st === 'locked'}
                    title={lesson.title}
                    meta={
                      st === 'passed'
                        ? t('lessons.completed')
                        : st === 'locked'
                          ? t('lessons.upNext')
                          : lesson.id === current.lessonId
                            ? t('marathon.now')
                            : t('lessons.open')
                    }
                    metaAccent={lesson.id === current.lessonId}
                    to={st === 'locked' ? undefined : `/app/lessons/${lesson.id}`}
                    id={`lesson-${lesson.id}`}
                    aside={
                      grades.has(lesson.id) ? (
                        <LessonGradeChip
                          curatorGrade={grades.get(lesson.id)}
                          expanded={expanded === lesson.id}
                          onToggle={() => toggleFeedback(lesson.id)}
                        />
                      ) : undefined
                    }
                    below={
                      expanded === lesson.id && grades.has(lesson.id) ? (
                        <LessonFeedbackPanel curatorGrade={grades.get(lesson.id)} className="mb-3" />
                      ) : undefined
                    }
                    leading={
                      st === 'passed' ? (
                        <MIcon icon="check" tone="done" />
                      ) : st === 'locked' ? (
                        // ⚠️ Номер урока, а не замок (решение владельца 14.09.2026).
                        <span className="grid h-8 w-8 flex-none place-items-center rounded-xl bg-surface-container-high text-[12px] font-bold text-on-surface-variant">
                          {i + 1}
                        </span>
                      ) : (
                        <MIcon icon="play_arrow" tone="warm" />
                      )
                    }
                  />
                );
              })}
            </MBlock>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <Link
        to="/app/lessons"
        className="inline-flex items-center gap-1 text-sm font-medium text-on-surface-variant transition hover:text-on-surface"
      >
        <span className="material-symbols-outlined text-lg">chevron_left</span>
        {t('marathon.back')}
      </Link>

      {marathons.isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : marathons.error ? (
        <Card variant="subtle" className="text-error">
          {humanizeError(marathons.error)}
        </Card>
      ) : !marathon ? (
        <Card variant="subtle" className="text-center text-on-surface-variant">
          {t('marathon.notFound')}
        </Card>
      ) : (
        <>
          {courseCard(marathon, 'desktop')}

          {/* Modules */}
          {modules.length === 0 ? (
            <Card variant="subtle" className="text-sm text-on-surface-variant">
              {t(emptyKey)}
            </Card>
          ) : (
            <ul id="course-lessons" className="scroll-mt-4 space-y-3">
              {modules.map((mod, idx) => (
                <ModuleCard
                  key={mod.id}
                  module={mod}
                  index={idx + 1}
                  open={openId === mod.id}
                  currentLessonId={current.lessonId}
                  onToggle={() => setOpen((cur) => (cur === mod.id ? '' : mod.id))}
                  grades={grades}
                  expandedLessonId={expanded}
                  onToggleFeedback={toggleFeedback}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function ModuleCard({
  module,
  index,
  open,
  currentLessonId,
  onToggle,
  grades,
  expandedLessonId,
  onToggleFeedback,
}: {
  module: Module;
  index: number;
  open: boolean;
  currentLessonId?: string;
  onToggle: () => void;
  grades: Map<string, CuratorGrade>;
  expandedLessonId: string | null;
  onToggleFeedback: (lessonId: string) => void;
}) {
  const { t } = useTranslation();
  const lessons = module.lessons ?? [];
  const done = lessons.filter((l) => statusOf(l) === 'passed').length;
  const hasCurrent = lessons.some((l) => l.id === currentLessonId);
  const allLocked = lessons.length > 0 && lessons.every((l) => statusOf(l) === 'locked');

  return (
    <Card
      className={cn(
        'p-0 overflow-hidden transition',
        hasCurrent && 'ring-2 ring-primary/50',
      )}
    >
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-3 p-4 text-left transition hover:bg-surface-container-low"
      >
        <span
          className={cn(
            'grid h-9 w-9 shrink-0 place-items-center rounded-button text-sm font-bold',
            allLocked
              ? 'bg-surface-container text-on-surface-variant'
              : 'bg-primary-fixed text-on-primary-fixed-variant',
          )}
        >
          {/* Всегда номер модуля — замка нет (решение владельца 14.09.2026). */}
          {index}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-display text-sm font-bold tracking-display text-on-surface">
            {module.title}
          </h3>
          <p className="text-xs text-on-surface-variant">
            {t('marathon.lessonsCount', { count: lessons.length })}
          </p>
        </div>
        <Pill variant={done === lessons.length && lessons.length > 0 ? 'success' : 'primary'}>
          {done}/{lessons.length}
        </Pill>
        <span className={cn('material-symbols-outlined transition', open && 'rotate-180')}>expand_more</span>
      </button>

      {open && lessons.length > 0 && (
        <div className="space-y-1.5 border-t border-outline-variant/20 bg-surface-container-low p-3">
          {lessons.map((lesson, i) => (
            <LessonRow
              key={lesson.id}
              lesson={lesson}
              number={i + 1}
              isCurrent={lesson.id === currentLessonId}
              curatorGrade={grades.get(lesson.id)}
              feedbackOpen={expandedLessonId === lesson.id && grades.has(lesson.id)}
              onToggleFeedback={() => onToggleFeedback(lesson.id)}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function LessonRow({
  lesson,
  number,
  isCurrent,
  curatorGrade,
  feedbackOpen,
  onToggleFeedback,
}: {
  lesson: Lesson;
  /** Номер урока в модуле — на месте замка у ещё не открытого урока. */
  number: number;
  isCurrent: boolean;
  curatorGrade?: CuratorGrade;
  feedbackOpen: boolean;
  onToggleFeedback: () => void;
}) {
  const { t } = useTranslation();
  const status = statusOf(lesson);
  const locked = status === 'locked';
  const passed = status === 'passed';
  // ⚠️ Замка нет (решение владельца 14.09.2026, Apple 3.1.3(f)): у ещё не открытого урока —
  // номер и «Дальше». Порядок прохождения прежний, решает сервер; строка не нажимается и
  // выглядит так: приглушённая, обычный курсор, без тени при наведении.
  const icon = passed ? 'check' : 'play_arrow';
  const to = `/app/lessons/${lesson.id}`;

  const lead = (
    <>
      <span
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
          passed
            ? 'bg-success-container text-on-success-container'
            : locked
              ? 'bg-surface-container text-on-surface-variant'
              : 'bg-primary text-on-primary',
        )}
      >
        {locked ? (
          <span className="text-sm font-bold">{number}</span>
        ) : (
          <span className="material-symbols-outlined text-xl">{icon}</span>
        )}
      </span>
      <span className={cn('flex-1 text-sm', isCurrent ? 'font-bold text-on-surface' : 'font-semibold text-on-surface')}>
        {lesson.title}
      </span>
    </>
  );

  const tail = passed ? (
    <Pill variant="success">{t('lessons.lessonPassed')}</Pill>
  ) : locked ? (
    <span className="text-xs font-semibold text-on-surface-variant">{t('lessons.upNext')}</span>
  ) : isCurrent ? (
    <Pill variant="primary">{t('marathon.start')}</Pill>
  ) : lesson.points ? (
    <Pill variant="primary">+{lesson.points}</Pill>
  ) : null;

  // ⚠️ ССЫЛКА — НЕ ВСЯ СТРОКА (D11): чип оценки и стрелка стоят между названием
  // и пилюлей, и внутри ссылки нажатие на них уводило бы в урок.
  return (
    <div
      id={`lesson-${lesson.id}`}
      className={cn(
        'rounded-button transition',
        isCurrent
          ? 'bg-primary-container/60 ring-1 ring-primary/40'
          : 'bg-surface-container-lowest',
        locked ? 'cursor-default opacity-50' : 'hover:shadow-soft',
      )}
    >
      <div className="flex items-center gap-3 pr-3">
        {locked ? (
          <div className="flex min-w-0 flex-1 items-center gap-3 py-3 pl-3">{lead}</div>
        ) : (
          <Link to={to} className="flex min-w-0 flex-1 items-center gap-3 py-3 pl-3">
            {lead}
          </Link>
        )}
        <LessonGradeChip curatorGrade={curatorGrade} expanded={feedbackOpen} onToggle={onToggleFeedback} />
        {tail && !locked ? (
          <Link to={to} tabIndex={-1} aria-hidden="true" className="flex-none">
            {tail}
          </Link>
        ) : (
          tail
        )}
      </div>
      {feedbackOpen && (
        <LessonFeedbackPanel curatorGrade={curatorGrade} className="mx-3 mb-3 bg-surface-container-low" />
      )}
    </div>
  );
}
